import { AlertCircle, FileSearch02 } from '@untitledui/icons';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';

import { Button } from '@/lib/base/buttons/button';
import { FeaturedIcon } from '@/lib/base/foundations/featured-icon/featured-icon';
import { DiagnosticsReportModal } from '@/lib/diagnostics/DiagnosticsReportModal';
import { ReportProblemButton } from '@/lib/diagnostics/ReportProblemButton';
import { sendViewHealthBeacon, ViewHealthContext } from '@/lib/view-health';

import { ThemeToggle } from './components/ThemeToggle';

export default function ErrorPage() {
  const error = useRouteError();
  const viewHealth = useContext(ViewHealthContext);
  const [reportOpen, setReportOpen] = useState(false);
  const openReport = useCallback(() => setReportOpen(true), []);
  const closeReport = useCallback(() => setReportOpen(false), []);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const toggleDetails = useCallback(() => setDetailsOpen(open => !open), []);
  // Full page loads (not client-side navigation) on purpose: the error page
  // renders when the app has crashed, so recovery must not depend on the
  // crashed tree's router or state.
  const handleRefresh = useCallback(() => {
    window.location.reload();
  }, []);
  const handleGoHome = useCallback(() => {
    window.location.assign('/');
  }, []);
  // Latch so we emit at most one beacon per mount, even though the effect
  // re-runs as the provider populates `isRouteMonitored`/route pattern.
  const hasEmittedRef = useRef(false);

  const isRouteMonitored = viewHealth?.isRouteMonitored ?? false;
  const currentRoutePattern = viewHealth?.currentRoutePattern ?? '';

  useEffect(() => {
    if (hasEmittedRef.current) {
      return;
    }
    // Only emit error beacons for monitored routes — unmonitored routes must
    // not produce metrics (acceptance criteria gate). The provider sets
    // `isRouteMonitored` inside its own effect on mount, so this effect must
    // re-run when that state changes rather than firing once with stale false.
    if (!isRouteMonitored) {
      return;
    }

    let errorType: string;
    if (isRouteErrorResponse(error)) {
      errorType = `http_${error.status}`;
    } else if (error instanceof Error) {
      errorType = error.name;
    } else {
      errorType = 'unknown';
    }

    hasEmittedRef.current = true;
    sendViewHealthBeacon({
      scope_type: 'route',
      scope_name: currentRoutePattern || window.location.pathname,
      outcome: 'error',
      error_type: errorType,
      duration_ms: 0,
    });
  }, [isRouteMonitored, currentRoutePattern, error]);

  let errorMessage: string;

  // ht: https://github.com/remix-run/react-router/discussions/9628#discussioncomment-5555901
  if (isRouteErrorResponse(error)) {
    // error is type `ErrorResponse`
    errorMessage = `${error.statusText}${error.data ? ': ' + error.data : ''}`;
  } else if (error instanceof Error) {
    errorMessage = error.message;
  } else if (typeof error === 'string') {
    errorMessage = error;
  } else {
    console.error(error);
    errorMessage = 'Unknown error';
  }

  // A 404 is a navigation miss, not an application fault — it gets "not
  // found" copy and a way home instead of crash-recovery affordances.
  const isNotFound = isRouteErrorResponse(error) && error.status === 404;

  return (
    <>
      <div className='relative flex h-full items-center justify-center p-8'>
        {/* Theme control stays available (the crash page may be all that
            renders) but out of the content column. */}
        <div className='absolute top-4 right-4'>
          <ThemeToggle />
        </div>

        <div className='flex w-full max-w-md flex-col items-center text-center'>
          <FeaturedIcon
            icon={isNotFound ? FileSearch02 : AlertCircle}
            size='lg'
            theme='light-outline'
            color={isNotFound ? 'gray' : 'error'}
          />

          <h1 className='mt-4 text-lg font-semibold text-[var(--color-text-primary)]'>
            {isNotFound ? 'Page not found' : 'Something went wrong'}
          </h1>
          <p className='mt-1 text-sm text-[var(--color-text-secondary)]'>
            {isNotFound
              ? "The page you're looking for doesn't exist or may have moved."
              : 'The page hit an unexpected error. Refreshing usually clears it – if it keeps happening, send us a report.'}
          </p>

          <div className='mt-6 flex items-center gap-3'>
            {isNotFound ? (
              <Button color='primary' size='md' onClick={handleGoHome}>
                Go to dashboard
              </Button>
            ) : (
              <>
                <Button color='primary' size='md' onClick={handleRefresh}>
                  Refresh page
                </Button>
                <ReportProblemButton onClick={openReport} />
              </>
            )}
          </div>

          {/* Technical detail stays available for support conversations but
              never leads: it is opt-in, visually quiet, and clearly labeled
              as technical (see TONE_STYLE_GUIDE — never expose system
              internals as the primary error copy). */}
          {!isNotFound && (
            <div className='mt-8 flex w-full flex-col items-center'>
              <Button
                color='link-gray'
                size='sm'
                onClick={toggleDetails}
                aria-expanded={detailsOpen}
              >
                {detailsOpen
                  ? 'Hide technical details'
                  : 'Show technical details'}
              </Button>
              {detailsOpen && (
                <pre className='mt-3 w-full overflow-x-auto rounded-lg border border-[var(--color-border-secondary)] bg-[var(--color-bg-secondary)] p-3 text-left font-mono text-xs whitespace-pre-wrap break-words text-[var(--color-text-secondary)]'>
                  {errorMessage}
                </pre>
              )}
            </div>
          )}
        </div>
      </div>
      <DiagnosticsReportModal
        isOpen={reportOpen}
        onClose={closeReport}
        trigger='error_page'
      />
    </>
  );
}
